// Snap the dock firefly back when the tab anchor jumps (drag reposition,
// tour handoff) while a portaled bug is still at a stale viewport point.

import { useEffect, useRef } from "react";

export const FIREFLY_DOCK_ANCHOR_JUMP_RECOVER_PX = 24;

export function useFireflyDockMotionRecover(options: {
  enabled: boolean;
  tourActive: boolean;
  anchorScreenX: number;
  anchorScreenY: number;
  portaledFireflyX: number;
  portaledFireflyY: number;
  portaled: boolean;
  onRecover: () => void;
}): void {
  const {
    enabled,
    tourActive,
    anchorScreenX,
    anchorScreenY,
    portaledFireflyX,
    portaledFireflyY,
    portaled,
    onRecover,
  } = options;

  const onRecoverRef = useRef(onRecover);
  onRecoverRef.current = onRecover;

  const prevAnchorRef = useRef({ x: anchorScreenX, y: anchorScreenY });
  const prevTourActiveRef = useRef(tourActive);

  useEffect(() => {
    if (prevTourActiveRef.current && !tourActive) {
      onRecoverRef.current();
    }
    prevTourActiveRef.current = tourActive;
  }, [tourActive]);

  useEffect(() => {
    if (!enabled || tourActive) {
      prevAnchorRef.current = { x: anchorScreenX, y: anchorScreenY };
      return;
    }

    const prev = prevAnchorRef.current;
    const anchorMoved = Math.hypot(
      anchorScreenX - prev.x,
      anchorScreenY - prev.y,
    );
    prevAnchorRef.current = { x: anchorScreenX, y: anchorScreenY };

    if (anchorMoved <= FIREFLY_DOCK_ANCHOR_JUMP_RECOVER_PX) return;

    if (!portaled) {
      onRecoverRef.current();
      return;
    }

    const bugDist = Math.hypot(
      portaledFireflyX - anchorScreenX,
      portaledFireflyY - anchorScreenY,
    );
    if (bugDist > FIREFLY_DOCK_ANCHOR_JUMP_RECOVER_PX) {
      onRecoverRef.current();
    }
  }, [
    anchorScreenX,
    anchorScreenY,
    enabled,
    portaled,
    portaledFireflyX,
    portaledFireflyY,
    tourActive,
  ]);
}
